import type { QueryClientConfig } from "@tanstack/react-query";

import { QueryClient } from "@tanstack/react-query";

/**
 * React Query configuration with sensible defaults
 * - 5 minute cache time for data
 * - 0 second stale time (data is immediately stale)
 * - 3 retry attempts with exponential backoff
 * - 30 second cache GC time
 */
const queryConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      // Time data is kept in memory before it's garbage collected
      gcTime: 30 * 60 * 1000, // 30 minutes

      // Time before data is considered "stale"
      // 0 = immediately stale, triggers background refetch
      staleTime: 0,

      // Retry failed requests up to 3 times
      retry: 3,

      // Exponential backoff: 1s, 2s, 4s
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Don't refetch in background by default
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
    mutations: {
      // Don't retry mutations by default
      retry: 0,
    },
  },
};

/**
 * Singleton QueryClient instance
 * Exported to be used in QueryClientProvider
 */
export const queryClient = new QueryClient(queryConfig);

/**
 * Helper to reset all queries and mutations
 * Useful for logout or account switching
 */
export function resetQueryClient(): void {
  queryClient.clear();
}

/**
 * Helper to clear all cached data
 * Useful for debugging or testing
 */
export function clearQueryCache(): void {
  queryClient.removeQueries();
}

/**
 * Helper to invalidate all queries
 * Forces refetch of all data on next use
 */
export async function invalidateAllQueries(): Promise<void> {
  await queryClient.invalidateQueries();
}
